import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { liveStatusTranslationKey } from '../../core/live-updates/live-update.models';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { bindInventoryLiveUpdates } from './inventory-live-updates';
import {
  InventoryOfficialReportsApiService,
  InventoryMainSimilarFlowLine,
  InventoryConsignmentLine,
  InventoryConsumptionAbcLine,
  InventoryOpeningInventoryLine,
  InventoryComponentMovementLine,
  InventoryMovementTypeLine,
  InventoryFulfillmentBacklogLine
} from './inventory-official-reports-api.service';
import { isCapabilityDisabledError, isPermissionDeniedError } from './inventory-api-error';

type ReportTab =
  | 'mainSimilar'
  | 'consignment'
  | 'consumptionAbc'
  | 'openingInventory'
  | 'componentMovements'
  | 'movementTypes'
  | 'fulfillmentBacklog';

@Component({
  standalone: true,
  selector: 'ain-inventory-official-reports-hub',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, JalaliDateInputComponent,
    FormsModule, TabsModule, TableModule, SelectModule, ButtonModule, InputNumberModule
  ],
  template: `
    <ain-page [title]="t('catalog.reports.title')" [description]="t('catalog.reports.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.reports.help.title'"
        [descriptionKey]="'catalog.reports.help.description'"
        [bulletKeys]="['catalog.reports.help.bullets.operational', 'catalog.reports.help.bullets.export']" />
      <span class="live-status" [class]="liveUpdateService.connectionState()">{{ t(liveStatusTranslationKey(liveUpdateService.connectionState())) }}</span>

      @if (capabilityDisabled()) {
        <ain-state type="empty" [title]="t('catalog.reports.disabledTitle')" [message]="t('catalog.reports.disabledDescription')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('catalog.inventory.states.noPermission')" [message]="t('catalog.inventory.states.noPermissionMessage')" />
      } @else if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="filters">
          <label><span>{{ t('catalog.inventory.warehouseId') }}</span>
            <p-select [(ngModel)]="warehouseFilter" [options]="warehouseOptions()" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" />
          </label>
          <label><span>{{ t('catalog.inventory.offeringId') }}</span>
            <p-select [(ngModel)]="offeringFilter" [options]="offeringOptions()" optionLabel="label" optionValue="value" [showClear]="true" [filter]="true" appendTo="body" />
          </label>
          @if (showDateFilters()) {
            <label><span>{{ t('catalog.reports.fromDate') }}</span><ain-date-input [(ngModel)]="fromDate" /></label>
            <label><span>{{ t('catalog.reports.toDate') }}</span><ain-date-input [(ngModel)]="toDate" /></label>
          }
          @if (activeTab() === 'consumptionAbc') {
            <label><span>{{ t('catalog.reports.windowDays') }}</span>
              <p-select [(ngModel)]="windowDays" [options]="windowOptions()" optionLabel="label" optionValue="value" appendTo="body" />
            </label>
          }
          @if (activeTab() === 'openingInventory') {
            <label><span>{{ t('catalog.reports.fiscalYear') }}</span>
              <p-inputNumber [(ngModel)]="fiscalYear" [useGrouping]="false" />
            </label>
          }
          <button pButton type="button" [label]="t('catalog.reports.refresh')" (click)="refresh()"></button>
          <button pButton type="button" severity="secondary" [label]="t('catalog.reports.exportCsv')" (click)="exportActive()"></button>
        </section>

        <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
          <p-tablist>
            <p-tab value="mainSimilar">{{ t('catalog.reports.tabs.mainSimilar') }}</p-tab>
            <p-tab value="consignment">{{ t('catalog.reports.tabs.consignment') }}</p-tab>
            <p-tab value="consumptionAbc">{{ t('catalog.reports.tabs.consumptionAbc') }}</p-tab>
            <p-tab value="openingInventory">{{ t('catalog.reports.tabs.openingInventory') }}</p-tab>
            <p-tab value="componentMovements">{{ t('catalog.reports.tabs.componentMovements') }}</p-tab>
            <p-tab value="movementTypes">{{ t('catalog.reports.tabs.movementTypes') }}</p-tab>
            <p-tab value="fulfillmentBacklog">{{ t('catalog.reports.tabs.fulfillmentBacklog') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="mainSimilar">
              <p-table [value]="mainSimilarLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.reports.columns.role') }}</th><th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.reports.columns.receiptQty') }}</th><th>{{ t('catalog.reports.columns.issueQty') }}</th>
                  <th>{{ t('catalog.reports.columns.consumptionQty') }}</th><th>{{ t('catalog.reports.columns.value') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ row.relationRole }}</td><td>{{ offeringLabel(row.offeringId) }}</td>
                  <td>{{ row.receiptQuantity }}</td><td>{{ row.issueQuantity }}</td>
                  <td>{{ row.consumptionQuantity }}</td><td>{{ row.consumptionValue }} {{ row.currencyCode }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="consignment">
              <p-table [value]="consignmentLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.reports.columns.direction') }}</th><th>{{ t('catalog.reports.columns.owner') }}</th>
                  <th>{{ t('catalog.inventory.offeringId') }}</th><th>{{ t('catalog.inventory.quantity') }}</th><th>{{ t('catalog.reports.columns.value') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ row.direction }}</td><td>{{ row.ownerPartyReference }}</td>
                  <td>{{ offeringLabel(row.offeringId) }}</td><td>{{ row.quantity }}</td><td>{{ row.value }} {{ row.currencyCode }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="consumptionAbc">
              <p-table [value]="abcLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.reports.columns.abcClass') }}</th><th>{{ t('catalog.reports.columns.indicator') }}</th>
                  <th>{{ t('catalog.inventory.offeringId') }}</th><th>{{ t('catalog.reports.columns.consumptionQty') }}</th><th>{{ t('catalog.reports.columns.onHand') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ row.abcClass }}</td><td>{{ indicatorLabel(row.consumptionIndicator) }}</td>
                  <td>{{ offeringLabel(row.offeringId) }}</td><td>{{ row.consumptionQuantity }}</td><td>{{ row.onHandQuantity }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="openingInventory">
              @if (openingSummary()) {
                <p class="summary">{{ t('catalog.reports.openingSummary') }}: {{ openingSummary()!.totalQuantity }} / {{ openingSummary()!.totalValue }} {{ openingSummary()!.currencyCode }}
                  — {{ openingSummary()!.reconciled ? t('catalog.reports.reconciled') : t('catalog.reports.notReconciled') }}</p>
              }
              <p-table [value]="openingLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.inventory.offeringId') }}</th><th>{{ t('catalog.inventory.warehouseId') }}</th>
                  <th>{{ t('catalog.inventory.quantity') }}</th><th>{{ t('catalog.reports.columns.value') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ offeringLabel(row.offeringId) }}</td><td>{{ warehouseLabel(row.warehouseId) }}</td>
                  <td>{{ row.quantity }}</td><td>{{ row.value }} {{ row.currencyCode }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="componentMovements">
              <p-table [value]="componentLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.reports.columns.source') }}</th><th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.reports.columns.reference') }}</th><th>{{ t('catalog.inventory.quantity') }}</th><th>{{ t('catalog.reports.columns.value') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ row.sourceType }}</td><td>{{ offeringLabel(row.componentOfferingId) }}</td>
                  <td>{{ row.referenceNumber }}</td><td>{{ row.quantity }}</td><td>{{ row.value }} {{ row.currencyCode }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="movementTypes">
              @if (movementSummary()) {
                <p class="summary">{{ t('catalog.reports.movementReconciliation') }}:
                  {{ movementSummary()!.reconciled ? t('catalog.reports.reconciled') : t('catalog.reports.notReconciled') }}</p>
              }
              <p-table [value]="movementTypeLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.reports.columns.category') }}</th><th>{{ t('catalog.reports.columns.source') }}</th>
                  <th>{{ t('catalog.inventory.offeringId') }}</th><th>{{ t('catalog.inventory.quantity') }}</th><th>{{ t('catalog.reports.columns.value') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ row.category }}</td><td>{{ row.sourceKind }}</td>
                  <td>{{ offeringLabel(row.offeringId) }}</td><td>{{ row.quantity }}</td><td>{{ row.value }} {{ row.currencyCode }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
            <p-tabpanel value="fulfillmentBacklog">
              <p-table [value]="fulfillmentLines()" [paginator]="true" [rows]="pageSize" [first]="tableFirst()" [totalRecords]="totalCount()" [lazy]="true" [loading]="tableLoading()" (onLazyLoad)="onLazyLoad($event)">
                <ng-template pTemplate="header"><tr>
                  <th>{{ t('catalog.reports.columns.reference') }}</th><th>{{ t('catalog.inventory.offeringId') }}</th>
                  <th>{{ t('catalog.reports.columns.requested') }}</th><th>{{ t('catalog.reports.columns.reserved') }}</th>
                  <th>{{ t('catalog.reports.columns.shipped') }}</th><th>{{ t('columns.status') }}</th>
                </tr></ng-template>
                <ng-template pTemplate="body" let-row><tr>
                  <td>{{ row.sourceReference }}</td><td>{{ offeringLabel(row.offeringId) }}</td>
                  <td>{{ row.requestedQuantity }}</td><td>{{ row.reservedQuantity }}</td>
                  <td>{{ row.shippedQuantity }}</td><td>{{ row.statusSummary }}</td>
                </tr></ng-template>
              </p-table>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
        @if (error()) { <p class="error">{{ error() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .filters { display: flex; flex-wrap: wrap; gap: 12px; align-items: end; margin-bottom: 12px; }
    .filters label { display: grid; gap: 6px; min-width: 180px; }
    .summary { margin: 8px 0; font-size: 0.9rem; color: #334155; }
    .error { color: #b91c1c; }
    .live-status { font-size: 0.8rem; color: #6b7280; display: block; margin-bottom: 8px; }
    .live-status.connected { color: #166534; }
  `]
})
export class InventoryOfficialReportsHubComponent implements OnInit {
  private readonly reportsApi = inject(InventoryOfficialReportsApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contextService = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusTranslationKey = liveStatusTranslationKey;

  readonly loading = signal(true);
  readonly tableLoading = signal(false);
  readonly error = signal('');
  readonly capabilityDisabled = signal(false);
  readonly permissionDenied = signal(false);
  readonly activeTab = signal<ReportTab>('mainSimilar');
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);
  readonly totalCount = signal(0);
  readonly tableFirst = signal(0);
  readonly pageSize = 25;
  pageNumber = 1;

  private loadInFlight = false;
  private lastLazyFirst = -1;

  readonly mainSimilarLines = signal<InventoryMainSimilarFlowLine[]>([]);
  readonly consignmentLines = signal<InventoryConsignmentLine[]>([]);
  readonly abcLines = signal<InventoryConsumptionAbcLine[]>([]);
  readonly openingLines = signal<InventoryOpeningInventoryLine[]>([]);
  readonly openingSummary = signal<{ totalQuantity: number; totalValue: number; currencyCode: string; reconciled: boolean } | null>(null);
  readonly componentLines = signal<InventoryComponentMovementLine[]>([]);
  readonly movementTypeLines = signal<InventoryMovementTypeLine[]>([]);
  readonly movementSummary = signal<{ reconciled: boolean } | null>(null);
  readonly fulfillmentLines = signal<InventoryFulfillmentBacklogLine[]>([]);

  warehouseFilter: string | null = null;
  offeringFilter: string | null = null;
  fromDate = '';
  toDate = '';
  windowDays = 90;
  fiscalYear = new Date().getFullYear();

  ngOnInit(): void {
    bindInventoryLiveUpdates(
      { liveUpdateService: this.liveUpdateService, contextService: this.contextService, destroyRef: this.destroyRef },
      () => void this.load(false)
    );
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o.filter(x => x.inventoryEnabled)) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    const tab = this.route.snapshot.queryParamMap.get('tab') as ReportTab | null;
    if (tab) this.activeTab.set(tab);
    this.loading.set(false);
  }

  refresh(): void {
    this.resetPagination();
    void this.load(true);
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    if (this.loadInFlight || this.capabilityDisabled() || this.permissionDenied()) {
      return;
    }

    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    if (first === this.lastLazyFirst && this.totalCount() > 0) {
      return;
    }

    this.lastLazyFirst = first;
    this.tableFirst.set(first);
    this.pageNumber = Math.floor(first / rows) + 1;
    void this.load(false);
  }

  private resetPagination(): void {
    this.pageNumber = 1;
    this.tableFirst.set(0);
    this.lastLazyFirst = -1;
    this.totalCount.set(0);
  }

  onTabChange(tab: string | number | undefined): void {
    if (!tab) return;
    this.activeTab.set(String(tab) as ReportTab);
    void this.router.navigate([], { queryParams: { tab }, queryParamsHandling: 'merge' });
    this.resetPagination();
    void this.load(true);
  }

  showDateFilters(): boolean {
    return ['mainSimilar', 'componentMovements', 'movementTypes'].includes(this.activeTab());
  }

  load(showLoading = true): void {
    if (this.loadInFlight) {
      return;
    }

    this.loadInFlight = true;
    if (showLoading) this.loading.set(true);
    else this.tableLoading.set(true);
    this.error.set('');
    this.capabilityDisabled.set(false);
    this.permissionDenied.set(false);

    const query = {
      fromDate: this.fromDate || undefined,
      toDate: this.toDate || undefined,
      warehouseId: this.warehouseFilter ?? undefined,
      offeringId: this.offeringFilter ?? undefined,
      pageNumber: this.pageNumber,
      pageSize: this.pageSize
    };

    const handleError = (err: unknown) => {
      if (isCapabilityDisabledError(err)) {
        this.capabilityDisabled.set(true);
        this.error.set('');
      } else if (isPermissionDeniedError(err)) {
        this.permissionDenied.set(true);
        this.error.set('');
      } else {
        this.error.set(this.t('catalog.reports.loadFailed'));
      }
      this.loading.set(false);
      this.tableLoading.set(false);
      this.loadInFlight = false;
    };

    const finishPage = (totalCount: number) => {
      this.totalCount.set(totalCount);
      this.loading.set(false);
      this.tableLoading.set(false);
      this.loadInFlight = false;
    };

    switch (this.activeTab()) {
      case 'mainSimilar':
        this.reportsApi.getMainSimilarFlow(query).subscribe({
          next: r => { this.mainSimilarLines.set(r.lines ?? []); finishPage(r.totalCount ?? 0); },
          error: handleError
        });
        break;
      case 'consignment':
        this.reportsApi.getConsignmentReport({
          offeringId: this.offeringFilter ?? undefined,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        }).subscribe({
          next: r => { this.consignmentLines.set(r.lines ?? []); finishPage(r.totalCount ?? 0); },
          error: handleError
        });
        break;
      case 'consumptionAbc':
        this.reportsApi.getConsumptionAbc({
          warehouseId: this.warehouseFilter ?? undefined,
          windowDays: this.windowDays,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        }).subscribe({
          next: r => { this.abcLines.set(r.lines); finishPage(r.totalCount ?? 0); },
          error: handleError
        });
        break;
      case 'openingInventory':
        this.reportsApi.getOpeningInventory({
          fiscalYear: this.fiscalYear,
          warehouseId: this.warehouseFilter ?? undefined,
          offeringId: this.offeringFilter ?? undefined,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        }).subscribe({
          next: r => {
            this.openingLines.set(r.lines);
            this.openingSummary.set({ totalQuantity: r.totalQuantity, totalValue: r.totalValue, currencyCode: r.currencyCode, reconciled: r.reconciled });
            finishPage(r.totalCount ?? 0);
          },
          error: handleError
        });
        break;
      case 'componentMovements':
        this.reportsApi.getComponentMovements(query).subscribe({
          next: r => { this.componentLines.set(r.lines ?? []); finishPage(r.totalCount ?? 0); },
          error: handleError
        });
        break;
      case 'movementTypes':
        this.reportsApi.getMovementTypes(query).subscribe({
          next: r => {
            this.movementTypeLines.set(r.lines);
            this.movementSummary.set({ reconciled: r.reconciled });
            finishPage(r.totalCount ?? 0);
          },
          error: handleError
        });
        break;
      case 'fulfillmentBacklog':
        this.reportsApi.getFulfillmentBacklog({
          warehouseId: this.warehouseFilter ?? undefined,
          offeringId: this.offeringFilter ?? undefined,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize
        }).subscribe({
          next: r => { this.fulfillmentLines.set(r.lines); finishPage(r.totalCount ?? 0); },
          error: handleError
        });
        break;
    }
  }

  exportActive(): void {
    let path = '';
    let params = new HttpParams();
    if (this.fromDate) params = params.set('fromDate', this.fromDate);
    if (this.toDate) params = params.set('toDate', this.toDate);
    if (this.warehouseFilter) params = params.set('warehouseId', this.warehouseFilter);
    if (this.offeringFilter) params = params.set('offeringId', this.offeringFilter);
    switch (this.activeTab()) {
      case 'mainSimilar': path = '/api/v1/inventory/reports/main-similar-flow/export'; break;
      case 'consignment': path = '/api/v1/inventory/reports/consignment/export'; break;
      case 'consumptionAbc':
        path = '/api/v1/inventory/reports/consumption-abc/export';
        params = params.set('windowDays', String(this.windowDays));
        break;
      case 'openingInventory':
        path = '/api/v1/inventory/reports/opening-inventory/export';
        params = params.set('fiscalYear', String(this.fiscalYear));
        break;
      case 'componentMovements': path = '/api/v1/inventory/reports/component-movements/export'; break;
      case 'movementTypes': path = '/api/v1/inventory/reports/movement-types/export'; break;
      case 'fulfillmentBacklog': path = '/api/v1/inventory/reports/fulfillment-backlog/export'; break;
    }
    if (!path) return;
    this.reportsApi.exportReport(path, params).subscribe({
      next: blob => {
        const url = URL.createObjectURL(new Blob(['\uFEFF', blob], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.activeTab()}-report-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.error.set(this.t('catalog.reports.exportFailed'))
    });
  }

  windowOptions = () => [
    { label: '30', value: 30 },
    { label: '90', value: 90 },
    { label: '180', value: 180 }
  ];
  offeringOptions = () => this.offerings().map(o => ({ label: `${o.code} — ${o.title}`, value: o.id }));
  warehouseOptions = () => this.warehouses().map(w => ({ label: `${w.code} — ${w.name}`, value: w.id }));
  offeringLabel = (id: string) => {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : id;
  };
  warehouseLabel = (id: string) => {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : id;
  };
  indicatorLabel = (key: string) => {
    const translated = this.t(`catalog.reports.indicators.${key}`);
    return translated === `catalog.reports.indicators.${key}` ? key : translated;
  };
  t(key: string): string { return this.localization.translate(key); }
}
